import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../axios";
import { buildApiV3Url } from "../shared/Utils";
import { AppDispatch, RootState } from "./types";

interface AuiWorkflowData {
    isCertificateTaskCompleted: boolean | null;
    isCustomerTaskCompleted: boolean | null;
    isSettingTaskCompleted: boolean | null;
    isVendorTaskCompleted: boolean | null;
}

export enum AuiWorkflowTaskCategory {
    CERTIFICATE = "Certificate",
    CUSTOMER = "Customer",
    SETTINGS = "Settings",
    VENDOR = "Vendor",
}

export const AuiWorkflowTaskCategoryToApiSegment = {
    [AuiWorkflowTaskCategory.CERTIFICATE]: "isCertificateTaskCompleted",
    [AuiWorkflowTaskCategory.CUSTOMER]: "isCustomerTaskCompleted",
    [AuiWorkflowTaskCategory.SETTINGS]: "isSettingTaskCompleted",
    [AuiWorkflowTaskCategory.VENDOR]: "isVendorTaskCompleted",
};

export interface AuiWorkflowState {
    isLoading: boolean;
    auiWorkflowData: AuiWorkflowData;
}

const initialState: AuiWorkflowState = {
    isLoading: false,
    auiWorkflowData: {
        isCertificateTaskCompleted: null,
        isCustomerTaskCompleted: null,
        isSettingTaskCompleted: null,
        isVendorTaskCompleted: null,
    },
};

const auiWorkflowSlice = createSlice({
    name: "auiWorkflow",
    initialState,
    reducers: {
        setIsLoading: (state: AuiWorkflowState, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setAuiWorkflowData: (state: AuiWorkflowState, action: PayloadAction<AuiWorkflowData>) => {
            state.auiWorkflowData = action.payload;
        },
    },
});

export const { setIsLoading, setAuiWorkflowData } = auiWorkflowSlice.actions;

export const selectIsLoading = (state: RootState) => state.auiWorkflow.isLoading;

export const updateAuiWorkflowTask =
    (taskCategory: AuiWorkflowTaskCategory) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
        const { isLoading, auiWorkflowData } = getState().auiWorkflow;
        const apiSegment = AuiWorkflowTaskCategoryToApiSegment[taskCategory];
        const apiSegmentValue = auiWorkflowData[apiSegment as keyof AuiWorkflowData];
        if (isLoading || apiSegmentValue) return;
        dispatch(setIsLoading(true));
        try {
            const response = await axiosInstance.post(
                buildApiV3Url(`aui-workflow/task/${taskCategory}/validate`),
                {},
                {
                    withCredentials: true,
                }
            );
            if (response.status === 200) {
                dispatch(setAuiWorkflowData({ ...auiWorkflowData, [apiSegment]: true }));
            }
            dispatch(setIsLoading(false));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error validating AUI workflow task:", error);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export default auiWorkflowSlice.reducer;
